// Картата на една сметка на телефон. Общата транспозиция „ред на колона" би
// направила от 12 колони карта от 12 реда — на екран влиза един запис и нищо
// не е подредено по важност. Тук първият ред носи това, което човек търси.

import { useTranslation } from "react-i18next";

import type { BillHistory } from "../../../../api/reportsBillsHistory";
import { fmtEuro } from "../../../../utils/currency";
import {
  EM_DASH,
  formatDateTime,
  formatMinutes,
  parseDecimal,
} from "../../../../components/dataTable";

type Props = {
  bill: BillHistory;
  waiterName: string;
  paymentName: string;
  /** false = сесия БЕЗ втори код — тихият признак не се рисува (както в КПИ-то). */
  sessionAccess: boolean;
};

export const BillsHistoryMobileCard = ({
  bill,
  waiterName,
  paymentName,
  sessionAccess,
}: Props) => {
  const { t } = useTranslation();
  const isOpen = bill.status === "open";
  const opened = formatDateTime(bill.created_at, false);
  const closed = formatDateTime(bill.closed_at, false);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        {/* Същият тих признак като в таблицата: контур вместо запълване при
            закрита сметка без номер на бон. Без надпис. */}
        <span
          aria-hidden
          className={`size-[9px] shrink-0 rounded-full ${
            isOpen
              ? "bg-brand"
              : bill.receipt_number || !sessionAccess
                ? "bg-brand"
                : "border-[1.5px] border-brand bg-transparent"
          }`}
        />
        <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-ink">
          {bill.name}
        </span>
        <span className="font-pos-mono text-[15px] font-bold tabular-nums text-ink">
          {fmtEuro(parseDecimal(bill.total_amount))}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-muted">
        <span className="truncate">{waiterName || EM_DASH}</span>
        <span aria-hidden>·</span>
        <span className="font-pos-mono tabular-nums">
          {opened.time}
          {isOpen ? "" : ` – ${closed.time}`}
        </span>
        <span aria-hidden>·</span>
        <span className="font-pos-mono tabular-nums">
          {formatMinutes(bill.duration_minutes, {
            hour: t("dataTable.hourShort"),
            minute: t("dataTable.minuteShort"),
          })}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-soft">
        <span>
          {isOpen ? t("reportsPage.statusOpenOne") : paymentName || EM_DASH}
        </span>
        {bill.receipt_number ? (
          <>
            <span aria-hidden>·</span>
            <span className="font-pos-mono tabular-nums">
              {t("reportsPage.receiptNumber")} {bill.receipt_number}
            </span>
          </>
        ) : null}
        {/* Само когато сметката Е фактурирана. „Не" не се пише: липсата на
            чипа сама го казва, а редът и без това е тесен. */}
        {bill.is_invoiced ? (
          <span className="rounded-md bg-brand-tint px-1.5 py-[1px] text-[11px] font-bold leading-[normal] text-brand-deep-ink">
            {t("reportsPage.invoice")}
          </span>
        ) : null}
        {/* Възстановена (отворена наново със същото id) — също само при „да". */}
        {(bill.restores_count ?? 0) > 0 ? (
          <span className="rounded-md bg-info-tint px-1.5 py-[1px] text-[11px] font-bold leading-[normal] text-info-deep">
            {t("reportsPage.restored")}
          </span>
        ) : null}
      </div>
    </div>
  );
};
